import React, { useState, useEffect } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import { Fuel, Sun, Zap, AlertTriangle, CheckCircle2,
         TrendingDown, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import EnergyMixChart from "@/components/EnergyMixChart";

const SCENARIO_CONFIG = {
  diesel_price_shock: { label: "Diesel Price Shock",      color: "text-red-700",     bg: "bg-red-50 border-red-200", icon: Fuel },
  grid_improvement:   { label: "Grid Reliability +20%",  color: "text-blue-700",    bg: "bg-blue-50 border-blue-200", icon: Zap },
  solar_transition:   { label: "Solar Transition (40%)", color: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", icon: Sun },
};

const SEVERITY_STYLES = {
  high:   "bg-red-50 border-red-200 text-red-700",
  medium: "bg-amber-50 border-amber-200 text-amber-700",
  low:    "bg-emerald-50 border-emerald-200 text-emerald-700",
};

function InputField({ label, value, onChange, placeholder, unit, helpText }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number" step="any" min="0"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="flex-1 h-9 px-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#059669]"
          placeholder={placeholder}
        />
        {unit && <span className="text-xs text-slate-400 whitespace-nowrap">{unit}</span>}
      </div>
      {helpText && <p className="text-xs text-slate-400 mt-0.5">{helpText}</p>}
    </div>
  );
}

export default function NigeriaEnergyModule({ assessmentId, reportingYear }) {
  const [breakdown, setBreakdown] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [advisory, setAdvisory] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("input");
  const [form, setForm] = useState({
    country_code: "NG",
    grid_kwh: 0, diesel_kwh: 0, solar_kwh: 0, other_kwh: 0,
    diesel_liters_per_year: 0, diesel_cost_usd_per_year: 0, notes: ""
  });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    load();
    loadProfile();
  }, [assessmentId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await client.get(`/country/assessments/${assessmentId}/energy-breakdown`);
      if (res.data) {
        setBreakdown(res.data);
        setForm({
          country_code: res.data.country_code || "NG",
          grid_kwh: res.data.grid_kwh || 0,
          diesel_kwh: res.data.diesel_kwh || 0,
          solar_kwh: res.data.solar_kwh || 0,
          other_kwh: res.data.other_kwh || 0,
          diesel_liters_per_year: res.data.diesel_liters_per_year || 0,
          diesel_cost_usd_per_year: res.data.diesel_cost_usd_per_year || 0,
          notes: res.data.notes || ""
        });
      }
    } catch {}
    setLoading(false);
  };

  const loadProfile = async () => {
    try {
      const res = await client.get("/country/profiles/NG");
      setProfile(res.data);
    } catch {}
  };

  const save = async () => {
    setSaving(true);
    try {
      const total = form.grid_kwh + form.diesel_kwh + form.solar_kwh + form.other_kwh;
      const payload = { ...form, reporting_year: reportingYear, total_kwh: total };
      const res = await client.post(`/country/assessments/${assessmentId}/energy-breakdown`, payload);
      setBreakdown(res.data);
      toast.success("Energy breakdown saved");
      // Reload scenarios and advisory
      loadScenarios();
      loadAdvisory();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const loadScenarios = async () => {
    try {
      const res = await client.get(`/country/assessments/${assessmentId}/nigeria-scenarios`);
      setScenarios(res.data);
    } catch {}
  };

  const loadAdvisory = async () => {
    try {
      const res = await client.get(`/country/assessments/${assessmentId}/nigeria-advisory`);
      setAdvisory(res.data);
    } catch {}
  };

  const totalKwh = form.grid_kwh + form.diesel_kwh + form.solar_kwh + form.other_kwh;
  const dieselDepPct = totalKwh > 0 ? Math.round((form.diesel_kwh / totalKwh) * 100) : 0;
  const previewBreakdown = { ...form, total_kwh: totalKwh, diesel_dependency_pct: dieselDepPct };

  return (
    <div className="space-y-4" data-testid="nigeria-energy-module">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-emerald-500" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nigeria Energy Intelligence</span>
        {profile && (
          <span className="text-xs text-slate-400 ml-1">
            Grid: {profile.grid_emission_factor} kgCO₂e/kWh · Diesel gen: {profile.diesel_factor_per_kwh} kgCO₂e/kWh
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-0">
        {[["input", "Energy Input"], ["chart", "Energy Mix"], ["scenarios", "Scenarios"], ["advisory", "Advisory"]].map(([key, label]) => (
          <button key={key} onClick={() => {
            setActiveTab(key);
            if (key === "scenarios" && !scenarios && breakdown) loadScenarios();
            if (key === "advisory" && !advisory && breakdown) loadAdvisory();
          }} className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${activeTab === key ? "border-[#059669] text-[#059669]" : "border-transparent text-slate-500 hover:text-slate-700"}`} data-testid={`energy-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {/* INPUT TAB */}
      {activeTab === "input" && (
        <div className="space-y-4">
          {/* Country selector */}
          <div className="flex items-center gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Country Profile</label>
              <select value={form.country_code} onChange={e => setForm(f => ({...f, country_code: e.target.value}))}
                className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white">
                {["NG", "ZA", "KE", "GH", "ET"].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {/* Live diesel dep preview */}
            <div className={`flex-1 p-2.5 rounded-lg border text-xs ${dieselDepPct > 40 ? "bg-red-50 border-red-200" : dieselDepPct > 20 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`}>
              <p className="text-slate-500 mb-0.5">Diesel Dependency</p>
              <p className={`text-lg font-bold ${dieselDepPct > 40 ? "text-red-700" : dieselDepPct > 20 ? "text-amber-700" : "text-emerald-700"}`}>
                {dieselDepPct}%
              </p>
            </div>
          </div>

          {/* Electricity source inputs */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Annual Electricity by Source</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField label="Grid Electricity" value={form.grid_kwh}
                onChange={v => setForm(f => ({...f, grid_kwh: v}))}
                unit="kWh/yr" helpText="From utility/NEDC supply" placeholder="0" />
              <InputField label="Diesel Generator" value={form.diesel_kwh}
                onChange={v => setForm(f => ({...f, diesel_kwh: v}))}
                unit="kWh/yr" helpText="Output from diesel gen" placeholder="0"
                data-testid="diesel-kwh-input" />
              <InputField label="Solar / Renewable" value={form.solar_kwh}
                onChange={v => setForm(f => ({...f, solar_kwh: v}))}
                unit="kWh/yr" helpText="On-site PV / renewable" placeholder="0" />
              <InputField label="Other Sources" value={form.other_kwh}
                onChange={v => setForm(f => ({...f, other_kwh: v}))}
                unit="kWh/yr" helpText="LPG gen, biomass, etc." placeholder="0" />
            </div>
          </div>

          {/* Diesel detail */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Diesel Generator Detail</p>
            <div className="grid grid-cols-2 gap-4">
              <InputField label="Diesel Consumed" value={form.diesel_liters_per_year}
                onChange={v => setForm(f => ({...f, diesel_liters_per_year: v}))}
                unit="liters/yr" helpText="For precise Scope 1 calculation" placeholder="0" />
              <InputField label="Diesel Cost" value={form.diesel_cost_usd_per_year}
                onChange={v => setForm(f => ({...f, diesel_cost_usd_per_year: v}))}
                unit="USD/yr" helpText="Actual annual diesel spend" placeholder="0" />
            </div>
          </div>

          {/* Total */}
          <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <span className="text-xs font-medium text-slate-600">Total electricity</span>
            <span className="text-sm font-bold tabular-nums">{(totalKwh / 1000).toFixed(1)} MWh/yr</span>
          </div>

          <button onClick={save} disabled={saving || totalKwh === 0}
            className="w-full h-10 bg-[#059669] text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="save-energy-btn">
            {saving ? <><RefreshCw size={13} className="animate-spin" /> Saving...</> : "Save Energy Breakdown"}
          </button>

          {/* Launch calculator */}
          <button
            onClick={() => {
              const url = new URL("/calculator", window.location.origin);
              url.searchParams.set("assessment", assessmentId);
              url.searchParams.set("diesel_liters", String(form.diesel_liters_per_year || 0));
              url.searchParams.set("kwh", String(form.grid_kwh + form.diesel_kwh + form.solar_kwh));
              window.open(url.toString(), "_blank");
            }}
            className="w-full h-9 border border-[#059669] text-[#059669] text-sm font-medium rounded-lg hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
            data-testid="launch-calculator-btn"
          >
            <Sun size={13} /> Calculate Solar ROI →
          </button>
        </div>
      )}

      {/* CHART TAB */}
      {activeTab === "chart" && (
        <div>
          {totalKwh > 0 ? (
            <EnergyMixChart breakdown={previewBreakdown} />
          ) : breakdown ? (
            <EnergyMixChart breakdown={breakdown} />
          ) : (
            <div className="py-8 text-center text-xs text-slate-400">
              Enter energy data in the Input tab to see the mix chart.
            </div>
          )}
        </div>
      )}

      {/* SCENARIOS TAB */}
      {activeTab === "scenarios" && (
        <div className="space-y-3" data-testid="nigeria-scenarios">
          {!scenarios && (
            <div className="text-center py-4">
              <button onClick={loadScenarios} className="text-xs text-[#059669] hover:underline">Load Scenarios</button>
              {!breakdown && <p className="text-xs text-slate-400 mt-1">Save energy data first</p>}
            </div>
          )}
          {scenarios && (
            <>
              <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-500">
                <strong>Baseline:</strong> {scenarios.baseline?.total_tco2e?.toFixed(2)} tCO₂e/yr electricity ·
                Diesel: {scenarios.baseline?.diesel_dependency_pct}% ·
                Solar: {scenarios.baseline?.solar_share_pct}%
              </div>
              {Object.entries(scenarios.scenarios || {}).map(([k, sc]) => {
                const cfg = SCENARIO_CONFIG[k] || {};
                const Icon = cfg.icon || Zap;
                const isOpen = expanded === k;
                return (
                  <div key={k} className={`rounded-xl border overflow-hidden ${cfg.bg}`} data-testid={`scenario-${k}`}>
                    <button onClick={() => setExpanded(isOpen ? null : k)}
                      className="w-full flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className={cfg.color} />
                        <span className={`text-sm font-semibold ${cfg.color}`}>{sc.scenario_name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className={`text-xs font-bold ${sc.delta_tco2e < 0 ? "text-emerald-600" : sc.delta_tco2e > 0 ? "text-red-600" : "text-slate-500"}`}>
                            {sc.delta_tco2e > 0 ? "+" : ""}{sc.delta_tco2e.toFixed(2)} tCO₂e/yr
                          </p>
                          <p className={`text-xs ${sc.delta_cost_usd > 0 ? "text-red-600" : "text-emerald-600"}`}>
                            {sc.delta_cost_usd > 0 ? "+" : ""}${sc.delta_cost_usd.toLocaleString(undefined, {maximumFractionDigits: 0})}/yr
                          </p>
                        </div>
                        {isOpen ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 space-y-2">
                        <p className="text-xs text-slate-600 leading-relaxed">{sc.interpretation}</p>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {[
                            { label: "New emissions", value: `${sc.new_total_tco2e?.toFixed(2)} t` },
                            { label: "Diesel dep", value: `${sc.new_diesel_dep_pct?.toFixed(0)}%` },
                            { label: sc.additional_capex > 0 ? "Est. CapEx" : "Payback", value: sc.additional_capex > 0 ? `$${sc.additional_capex?.toLocaleString()}` : "No capex" },
                          ].map(({ label, value }) => (
                            <div key={label} className="p-2 bg-white/60 rounded-lg">
                              <p className="text-slate-400">{label}</p>
                              <p className="font-bold text-[#0F172A]">{value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <p className="text-xs text-slate-400">Based on NERC 2023 / IEA scenarios. Not investment advice.</p>
            </>
          )}
        </div>
      )}

      {/* ADVISORY TAB */}
      {activeTab === "advisory" && (
        <div className="space-y-3" data-testid="nigeria-advisory">
          {!advisory && (
            <div className="text-center py-4">
              <button onClick={loadAdvisory} className="text-xs text-[#059669] hover:underline">Load Advisory</button>
              {!breakdown && <p className="text-xs text-slate-400 mt-1">Save energy data first</p>}
            </div>
          )}
          {advisory && (
            <>
              {/* Energy emissions summary */}
              <div className="grid grid-cols-3 gap-4 text-xs">
                {[
                  { label: "Grid Emissions", value: `${advisory.advisory.grid_tco2e?.toFixed(2)} t`, color: "text-blue-600" },
                  { label: "Diesel Emissions", value: `${advisory.advisory.diesel_tco2e?.toFixed(2)} t`, color: "text-amber-600" },
                  { label: "Annual Diesel Cost", value: `$${(advisory.advisory.annual_diesel_cost_est || 0).toLocaleString()}`, color: "text-red-600" },
                ].map(({ label, value, color }) => (
                  <div key={label} className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                    <p className="text-slate-400 mb-0.5">{label}</p>
                    <p className={`text-sm font-bold tabular-nums ${color}`}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Insights */}
              <div className="space-y-2">
                {(advisory.advisory.insights || []).map((ins, i) => (
                  <div key={i} className={`p-3 rounded-xl border ${SEVERITY_STYLES[ins.severity] || SEVERITY_STYLES.low}`}>
                    <p className="text-xs font-semibold mb-1">{ins.title}</p>
                    <p className="text-xs leading-relaxed opacity-80 mb-2">{ins.detail}</p>
                    {ins.recommendation && (
                      <div className="flex items-start gap-2">
                        <TrendingDown size={11} className="mt-0.5 flex-shrink-0" />
                        <p className="text-xs font-medium">{ins.recommendation}</p>
                      </div>
                    )}
                    {ins.action && (
                      <p className="text-xs text-slate-500 mt-1 italic">Next step: {ins.action}</p>
                    )}
                  </div>
                ))}
              </div>

              {advisory.advisory.country_context && (
                <div className="p-3 bg-slate-50 border border-slate-100 rounded-lg text-xs text-slate-500 leading-relaxed">
                  <strong className="text-slate-600">Nigeria Context:</strong> {advisory.advisory.country_context}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
